import type { STChecklistItem, Jurisdiction } from '@st/sdk'
import { match } from '@st/pattern'

// The payment form has no name input. Save persists this generated name and
// subsequent payment edits regenerate it from the current details.
export function generateTaxPaymentChecklistItemName(
  context: Extract<STChecklistItem['context'], { type: 'tax_payment' }>
) {
  return match(context.method)
    .with('automatic_withdrawal', () => `Verify ${context.jurisdiction.name} tax withdrawal`)
    .otherwise(() =>
      match(context.paymentPurpose)
        .with('balance_due', () => `Pay ${context.jurisdiction.name} taxes`)
        .with('estimated_tax', () => `Pay ${context.jurisdiction.name} estimated taxes`)
        .exhaustive()
    )
}

export type TaxPayment = Extract<STChecklistItem['context'], { type: 'tax_payment' }>
export type PaymentMessage =
  | { type: 'jurisdictionChanged'; level: string }
  | { type: 'stateChanged'; identifier: string }
  | { type: 'localNameChanged'; name: string }
  | { type: 'purposeChanged'; purpose: TaxPayment['paymentPurpose'] }
  | { type: 'installmentChanged'; value: string }
  | { type: 'amountChanged'; value: string }
  | { type: 'methodChanged'; method: TaxPayment['method'] }
  | { type: 'bankNameChanged'; value: string }
  | { type: 'accountLastFourChanged'; value: string }

export function updatePayment(
  payment: TaxPayment,
  message: PaymentMessage,
  catalog: Jurisdiction[],
  returnYear: number
): TaxPayment {
  return match(message)
    .with({ type: 'jurisdictionChanged' }, ({ level }) => ({
      ...payment,
      jurisdiction: match(level)
        .with('federal', () => ({ level, code: 'US', name: 'Federal' }))
        .with('state', () => ({ level, code: '', name: '' }))
        .with('local', () => ({ level, name: '' }))
        .otherwise(() => {
          throw new Error('Unknown jurisdiction level')
        })
    }))
    .with({ type: 'stateChanged' }, ({ identifier }) => {
      const entry = catalog.find((entry) => entry.identifier == identifier)!
      return { ...payment, jurisdiction: { level: 'state', code: identifier, name: entry.name } }
    })
    .with({ type: 'localNameChanged' }, ({ name }) => ({
      ...payment,
      jurisdiction: { ...payment.jurisdiction, name }
    }))
    .with({ type: 'purposeChanged' }, ({ purpose }) => {
      // Estimates accompanying a return apply to the following tax year.
      const taxYear = match(purpose)
        .with('balance_due', () => returnYear)
        .with('estimated_tax', () => returnYear + 1)
        .exhaustive()
      const next = { ...payment, paymentPurpose: purpose, taxYear }
      delete next.installment
      return next
    })
    .with({ type: 'installmentChanged' }, ({ value }) => {
      const next = { ...payment }
      if (value === '') delete next.installment
      else next.installment = Number(value)
      return next
    })
    .with({ type: 'amountChanged' }, ({ value }) => ({ ...payment, amount: value }))
    .with({ type: 'methodChanged' }, ({ method }) => {
      const next = { ...payment, method }
      delete next.bankAccount
      delete next.withdrawalOn
      return next
    })
    .with({ type: 'bankNameChanged' }, ({ value }) => ({
      ...payment,
      bankAccount: { name: value, last4: payment.bankAccount?.last4 ?? '' }
    }))
    .with({ type: 'accountLastFourChanged' }, ({ value }) => ({
      ...payment,
      bankAccount: { name: payment.bankAccount?.name ?? '', last4: value }
    }))
    .exhaustive()
}

export function selectPaymentFields(payment: TaxPayment, catalog: Jurisdiction[]) {
  return {
    payment,
    states: catalog.filter((entry) => entry.level == 'state'),
    bankName: payment.bankAccount?.name ?? '',
    last4: payment.bankAccount?.last4 ?? '',
    installment: String(payment.installment ?? ''),
    valid: payment.amount != '' && payment.jurisdiction.name != ''
  }
}
